import React, { useState, useEffect } from 'react';
import { api } from '../api';

const USE_TYPE_META = {
  radio:         { icon:'📻', label:'Radio Broadcast',    unit:'/mo',      category:'broadcast' },
  dj:            { icon:'🎧', label:'DJ Voiceovers',      unit:'/session', category:'broadcast' },
  audiobook:     { icon:'📚', label:'Audiobook',          unit:'/hr',      category:'content'   },
  podcast:       { icon:'🎙', label:'Podcast',            unit:'/ep',      category:'content'   },
  mc:            { icon:'🎤', label:'MC / Event Host',    unit:'/event',   category:'events'    },
  greeting:      { icon:'👋', label:'Personal Greeting',  unit:'/clip',    category:'consumer'  },
  call_a_friend: { icon:'📞', label:'Call a Friend',      unit:'/clip',    category:'consumer'  },
  commercial:    { icon:'📢', label:'Commercial / Ad',    unit:'/use',     category:'commercial'},
};

const CATEGORIES = [
  { id:'broadcast',  label:'Broadcast',  color:'#6366f1' },
  { id:'content',    label:'Content',    color:'#0891b2' },
  { id:'events',     label:'Events',     color:'#7c3aed' },
  { id:'consumer',   label:'Consumer',   color:'#16a34a' },
  { id:'commercial', label:'Commercial', color:'#ea580c' },
];

export default function UseTypeSettings({ personaId, voiceName, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [localPrices, setLocalPrices] = useState({});
  const [localPermitted, setLocalPermitted] = useState(['radio']);

  useEffect(() => {
    if (!personaId) return;
    api.get(`/broadcast/use-types/personas/${personaId}`)
      .then(r => {
        setSettings(r);
        setLocalPermitted(r.permitted_use_types || ['radio']);
        const prices = {};
        Object.entries(r.prices || {}).forEach(([k,v]) => {
          prices[k] = v.price;
        });
        setLocalPrices(prices);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [personaId]);

  const togglePermitted = (utId) => {
    if (utId === 'radio') return; // radio always on
    setLocalPermitted(prev =>
      prev.includes(utId) ? prev.filter(u=>u!==utId) : [...prev, utId]
    );
  };

  const save = async () => {
    setSaving(true); setSaved(false); setError(null);
    try {
      await api.put(`/broadcast/use-types/personas/${personaId}`, {
        permitted_use_types: localPermitted,
        blocked_use_types: [],
        price_per_use_type: localPrices,
      });
      setSaved(true);
      if (onSaved) onSaved();
      setTimeout(() => setSaved(false), 2500);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding:20, color:'#94a3b8', fontSize:13 }}>Loading use types…</div>;

  return (
    <div>
      <div style={{ fontSize:14, fontWeight:700, color:'#0f172a', marginBottom:6 }}>Use Type Controls</div>
      <div style={{ fontSize:12, color:'#64748b', marginBottom:20, lineHeight:1.5 }}>
        Control what {voiceName || 'this persona'} can be used for, and set individual prices per use type.
        Radio is always enabled. Consumer use types (Greeting, Call a Friend) are available to the public Voice Studio.
      </div>

      {CATEGORIES.map(cat => {
        const catTypes = Object.entries(USE_TYPE_META).filter(([_,m])=>m.category===cat.id);
        return (
          <div key={cat.id} style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:cat.color,
              textTransform:'uppercase', letterSpacing:0.5, marginBottom:8,
              display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:cat.color }} />
              {cat.label}
            </div>
            <div style={{ display:'grid', gap:8 }}>
              {catTypes.map(([utId, meta]) => {
                const isPermitted = localPermitted.includes(utId);
                const isRadio = utId === 'radio';
                const price = localPrices[utId] ?? (settings?.prices?.[utId]?.price ?? 0);

                return (
                  <div key={utId} style={{
                    background: isPermitted ? '#f8fafc' : '#fafafa',
                    borderRadius:10, padding:'12px 14px',
                    border:`1px solid ${isPermitted ? '#e2e8f0' : '#f1f5f9'}`,
                    opacity: isPermitted ? 1 : 0.6,
                    transition:'all 0.15s'
                  }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        {/* Toggle */}
                        <div onClick={()=>!isRadio && togglePermitted(utId)}
                          style={{ width:36, height:20, borderRadius:10,
                            background: isPermitted ? cat.color : '#e2e8f0',
                            position:'relative', cursor: isRadio ? 'default' : 'pointer',
                            transition:'background 0.2s', flexShrink:0 }}>
                          <div style={{ position:'absolute', top:2,
                            left: isPermitted ? 18 : 2, width:16, height:16,
                            borderRadius:'50%', background:'#fff',
                            transition:'left 0.2s', boxShadow:'0 1px 2px rgba(0,0,0,0.2)' }} />
                        </div>
                        <span style={{ fontSize:16 }}>{meta.icon}</span>
                        <div>
                          <div style={{ fontSize:13, fontWeight:600, color:'#0f172a' }}>{meta.label}</div>
                          {isRadio && <div style={{ fontSize:10, color:'#94a3b8' }}>Always enabled</div>}
                        </div>
                      </div>

                      {/* Price input */}
                      {isPermitted && (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:13, color:'#374151' }}>$</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={price}
                            onChange={e=>setLocalPrices(p=>({...p,[utId]:parseFloat(e.target.value)||0}))}
                            disabled={isRadio}
                            style={{ width:70, padding:'5px 8px', border:'1px solid #e2e8f0',
                              borderRadius:7, fontSize:13, textAlign:'right',
                              background: isRadio ? '#f8fafc' : '#fff',
                              color: isRadio ? '#94a3b8' : '#0f172a' }} />
                          <span style={{ fontSize:11, color:'#94a3b8', minWidth:36 }}>{meta.unit}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && <div style={{ marginBottom:12, padding:10, background:'#fef2f2',
        borderRadius:8, fontSize:12, color:'#dc2626' }}>{error}</div>}

      <button onClick={save} disabled={saving}
        style={{ width:'100%', padding:'12px 0', borderRadius:10, border:'none',
          background: saved ? '#16a34a' : '#6366f1', color:'#fff',
          fontWeight:700, fontSize:13, cursor:saving?'wait':'pointer',
          transition:'background 0.2s' }}>
        {saving ? 'Saving…' : saved ? '✅ Saved' : 'Save Use Type Settings'}
      </button>
    </div>
  );
}
